import styled from '@emotion/styled'
import { AdUnit } from '@pubstack/common/src/adunit'
import { DeviceType } from '@pubstack/common/src/device-type'
import { byLabel } from '@pubstack/common/src/sort'
import { AdunitDeviceRuleConfig } from '@pubstack/common/src/stack'
import { capitalizeFirstLetter } from '@pubstack/common/src/stringUtils'
import { FunctionComponent, useEffect, useState } from 'react'
import { Controller, FieldArrayWithId, useFieldArray, UseFieldArrayReturn, useFormContext, useFormState, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { ChipListInput } from '~/components/ChipListInput'
import { Icon } from '~/components/Icon'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Message } from '~/components/Message'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { StackFormData } from '~/modules/adm/inventory/sites/stack/StackEdit'
import { WithClassName } from '~/types/utils'
import { ToggleWithText } from './ToggleWithText'

/* Thanks to:
 - https://www.piotrl.net/typescript-condition-subset-types/
 - https://stackoverflow.com/questions/74272393/how-to-distinguish-different-functions-signature-with-conditional-type-checks/74272675
 - https://github.com/microsoft/TypeScript/issues/27024

 we're looking for the fields implementing AdunitDeviceRuleConfig[]|undefined and return the relevant keys
 */
type KeyOfTypeInto<Structure, TypeSeeked> = keyof {
  [Field in keyof Structure as [Structure[Field]] extends [TypeSeeked] ? Field : never]: any
}
type AdUnitDeviceRuleFieldsFromStackFormData = KeyOfTypeInto<StackFormData, AdunitDeviceRuleConfig[] | undefined>

type CommonAdunitDeviceRulesSelectorProps = {
  fieldRulesName: 'refreshRules'
  availableRules: SelectOptionProp<string>[]
}

type DeviceAdunitDeviceRuleSelectorProps = CommonAdunitDeviceRulesSelectorProps & {
  device: DeviceType
  availableAdUnits: SelectOptionProp<string>[]
  currentData: AdunitDeviceRuleConfig[]
  fields: (FieldArrayWithId<StackFormData, AdUnitDeviceRuleFieldsFromStackFormData, 'id'> & { originalIndex: number })[]
} & Pick<UseFieldArrayReturn<StackFormData, AdUnitDeviceRuleFieldsFromStackFormData, 'id'>, 'append' | 'remove'>

type AdunitDeviceRuleSelectorProps = WithClassName &
  CommonAdunitDeviceRulesSelectorProps & {
    title: string
    extraInformation?: string
    availableDevices: DeviceType[]
    availableAdUnits: { [key in DeviceType]: AdUnit[] }
    description?: string
    disabledSelector?: boolean
  }

const Title = styled.h2`
  ${Fonts.H2};
  ${Colors.Jet};
  font-weight: 500;
`

const AdunitDeviceRuleSelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const AdunitDeviceRuleTitle = styled.span`
  display: inline-flex;
  gap: 12px;
`

const DeviceErrorMessageWrapper = styled.span`
  display: inline-flex;
  color: ${Colors.Alert};
  align-items: center;
  gap: 4px;
`

const DeviceAdunitDeviceRuleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;

  & > ${Message} {
    width: 760px;
    box-sizing: border-box;
  }

  & > ${Button} {
    align-self: flex-start;
  }
`
const InformationWrapper = styled.div`
  display: block;
  gap: 200px;
`
const ComplementInformationWrapper = styled.span`
  font-style: italic;
  display: block;
`
const AdUnitRuleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & > div {
    display: flex;
    gap: 12px;
    > :not(${Button}) {
      width: 760px;
    }
  }
`

const DeviceErrorMessage = ({ message }: { message?: string }) => (
  <DeviceErrorMessageWrapper>
    <Icon width={'16px'} name={'alert'} />
    {message}
  </DeviceErrorMessageWrapper>
)

const DeviceAdunitDeviceRuleSelector: FunctionComponent<DeviceAdunitDeviceRuleSelectorProps> = ({ currentData, availableAdUnits, device, availableRules, fieldRulesName, fields, append, remove }) => {
  const { control, setValue, getValues } = useFormContext<StackFormData>()
  const { errors } = useFormState({ control, name: `configuration.${fieldRulesName}` })

  const remainingAdUnits = availableAdUnits.filter((adUnit) => currentData?.find((field) => field.adUnitIds.includes(adUnit.value)) === undefined).sort(byLabel)
  const remainingRules = availableRules.filter((rule) => currentData?.find((field) => field.ruleId === rule.value) === undefined).sort(byLabel)

  const selectedAdUnits = currentData?.flatMap((rule) => rule.adUnitIds) ?? []
  const deviceLabel = `${capitalizeFirstLetter(device)} (${selectedAdUnits.length}/${availableAdUnits.length} ad units)`

  // type of error message defines the device on wich it applies
  const deviceErrorMessage = errors?.configuration?.[fieldRulesName]?.find?.((error) => error?.type === device)?.message
  const deviceAdUnitsErrorMessage = deviceErrorMessage ? <DeviceErrorMessage message={deviceErrorMessage} /> : null

  const addAvailableAdUnits = (value: AdunitDeviceRuleConfig, index: number) => {
    setValue(`configuration.${fieldRulesName}.${index}.adUnitIds`, [...value.adUnitIds, ...remainingAdUnits.map((adUnit) => adUnit.value)])
  }

  return (
    <DeviceAdunitDeviceRuleWrapper>
      <AdunitDeviceRuleTitle>
        <Title>{deviceLabel} </Title>
        <span>{deviceAdUnitsErrorMessage}</span>
      </AdunitDeviceRuleTitle>

      {fields.map((field, index) => {
        const selectedRule = currentData?.[index] ? availableRules.find((rule) => rule.value === currentData?.[index].ruleId) : undefined
        const rulesOptionsAvailable = [...remainingRules, selectedRule].filter((rule): rule is SelectOptionProp<string> => !!rule).sort(byLabel)
        return (
          <Controller
            key={field.id}
            control={control}
            name={`configuration.${fieldRulesName}.${field.originalIndex}`}
            rules={{
              validate: {
                // dirty trick : defining the validate rule by device here allows us to select it for display purposes
                [device]: () => selectedAdUnits.length === availableAdUnits.length || 'You must assign all available ad units to a rule for this device.',
              },
            }}
            render={({ field: { value } }) => {
              if (!value) return <></>
              return (
                <AdUnitRuleWrapper key={value.ruleId}>
                  <div>
                    <Select
                      searchable
                      isMessageBlock
                      rules={{
                        required: 'You must select a rule.',
                        validate: (ruleValue) => {
                          const value = getValues(`configuration.${fieldRulesName}.${field.originalIndex}`)
                          return !value?.adUnitIds.length && !!ruleValue ? `You must assign at least one ad unit to this rule or delete it.` : true
                        },
                      }}
                      error={errors?.configuration?.[fieldRulesName]?.[field.originalIndex]?.ruleId?.message}
                      control={control}
                      name={`configuration.${fieldRulesName}.${field.originalIndex}.ruleId`}
                      label={'Rule'}
                      options={rulesOptionsAvailable}
                    />
                    {index > 0 && (
                      <Button
                        variant={'tertiary'}
                        iconName={'delete'}
                        onClick={() => remove(field.originalIndex)}
                        trackingLabel={`adm-sites-stacks-form-${normalizeAsTrackingLabel(fieldRulesName)}-${device}-rule-delete`}
                      />
                    )}
                  </div>
                  <div>
                    <ChipListInput
                      isSpaceAllowed
                      label={'Apply rule to'}
                      control={control}
                      name={`configuration.${fieldRulesName}.${field.originalIndex}.adUnitIds`}
                      selectableOptions={remainingAdUnits}
                      error={errors?.configuration?.[fieldRulesName]?.[field.originalIndex]?.adUnitIds?.message}
                      rules={{
                        validate: () => {
                          return value.adUnitIds.every((id) => availableAdUnits.find((adUnit) => adUnit.value === id)) || 'Some ad units are no longer available for this device.'
                        },
                      }}
                    />
                    <Button
                      disabled={!remainingAdUnits.length}
                      variant={'tertiary'}
                      onClick={() => addAvailableAdUnits(value, field.originalIndex)}
                      trackingLabel={`adm-sites-stacks-form-${normalizeAsTrackingLabel(fieldRulesName)}-${device}-add-all-ad-units-apply`}
                    >
                      Add available ad units
                    </Button>
                  </div>
                </AdUnitRuleWrapper>
              )
            }}
          />
        )
      })}
      <Button
        disabled={remainingRules.length === 0 || remainingAdUnits.length === 0}
        variant={'secondary'}
        onClick={() => append({ adUnitIds: [], ruleId: '', device })}
        trackingLabel={`adm-sites-stacks-form-${normalizeAsTrackingLabel(fieldRulesName)}-${device}-rule-new`}
      >
        Add group
      </Button>
    </DeviceAdunitDeviceRuleWrapper>
  )
}

const BaseAdunitDeviceRuleSelector: FunctionComponent<AdunitDeviceRuleSelectorProps> = ({
  className,
  availableAdUnits,
  availableDevices,
  availableRules,
  fieldRulesName,
  title,
  extraInformation,
  description,
  disabledSelector,
}) => {
  const { control, trigger } = useFormContext<StackFormData>()
  const { fields, append, remove } = useFieldArray({
    control,
    name: `configuration.${fieldRulesName}`,
  })

  const formData = useWatch({ control, name: `configuration.${fieldRulesName}` })
  useEffect(() => {
    // have to trigger validation manually on form change because fieldArrays are not firing changes on their own if sub fields are changed
    trigger(`configuration.${fieldRulesName}`)
  }, [formData])

  const [isToggleChecked, setIsToggleChecked] = useState(!!formData?.length)
  const [formDataMemo, setFormDataMemo] = useState<AdunitDeviceRuleConfig[] | undefined>(formData)

  const devicesUsedToSelectRules = availableDevices.filter((device: DeviceType) => {
    return availableAdUnits[device] && availableAdUnits[device].length > 0
  })

  return (
    <Controller
      control={control}
      name={`configuration.${fieldRulesName}`}
      rules={{
        validate: (value) => {
          // no value means no control. On field array init, value is an empty array
          return !value?.length || devicesUsedToSelectRules.every((device) => value?.find((rule) => rule.device === device)) ? true : 'You must create at least a rule for each device.'
        },
      }}
      render={({ field: { onChange }, formState: { errors } }) => {
        return (
          <AdunitDeviceRuleSelectorWrapper className={className}>
            <ToggleWithText
              checked={isToggleChecked}
              onChange={(isChecked) => {
                setIsToggleChecked(isChecked)
                if (!isChecked) {
                  onChange(undefined)
                  setFormDataMemo(formData)
                } else {
                  onChange(formDataMemo)
                }
              }}
              trackingLabel={isToggleChecked ? `adm-sites-stacks-form-${normalizeAsTrackingLabel(fieldRulesName)}-unset` : `adm-sites-stacks-form-${normalizeAsTrackingLabel(fieldRulesName)}-set`}
              disabled={disabledSelector}
              title={title}
              description={description}
              errorMessage={isToggleChecked && errors?.configuration?.[fieldRulesName]?.root ? errors?.configuration?.[fieldRulesName]?.root?.message : undefined}
            />
            {isToggleChecked && (
              <>
                <InformationWrapper>
                  Create groups containing a rule and ad units that will use it.
                  {extraInformation && <ComplementInformationWrapper>{extraInformation}</ComplementInformationWrapper>}
                </InformationWrapper>
                {devicesUsedToSelectRules.map((device: DeviceType, index: number) => (
                  <DeviceAdunitDeviceRuleSelector
                    fieldRulesName={fieldRulesName}
                    currentData={formData?.filter((rule) => rule.device === device) ?? []}
                    key={index}
                    availableAdUnits={availableAdUnits[device].map((adUnit) => ({
                      label: adUnit.name,
                      value: adUnit.name,
                      trackingLabel: `adm-sites-stacks-form-${normalizeAsTrackingLabel(fieldRulesName)}-${device}-ad-unit-set`,
                    }))}
                    device={device}
                    availableRules={availableRules.map((rule) => ({
                      value: rule.value,
                      label: rule.label,
                      trackingLabel:
                        rule.value === 'NO_RULE'
                          ? `adm-sites-stacks-form-${normalizeAsTrackingLabel(fieldRulesName)}-${device}-no-rule-set`
                          : `adm-sites-stacks-form-${normalizeAsTrackingLabel(fieldRulesName)}-${device}-rule-set`,
                    }))}
                    fields={
                      // fields are common between devices, so we have to filter them by device and pass down the proper index for update purposes
                      fields
                        .map((field, index) => ({
                          // keep track of this specific field even after fields have been filtered
                          originalIndex: index,
                          ...field,
                        }))
                        .filter((field) => field.device === device)
                    }
                    append={append}
                    remove={remove}
                  />
                ))}
              </>
            )}
          </AdunitDeviceRuleSelectorWrapper>
        )
      }}
    />
  )
}

export const AdunitDeviceRuleSelector = styled(BaseAdunitDeviceRuleSelector)``
